import { makeLoopLineageKey } from './loopStepHelpers.js';
import { buildToolStepByKeyMapForThread, getSingleNonErrorOutputToolStepRoleAddress, projectChangedToolStepsToWorkflowStateDelta, rebindDownstreamInternalInputPotentials, toolStepRoleAddressEquals, } from './macroStepHelpers.js';
import { nullMacroStepContext, stampAndAppendTracePoint, toTopLevelStepPath } from './tracePointHelpers.js';
function findBranchChainRootIndex(threadSteps, currentBranchIndex) {
    let index = currentBranchIndex;
    while (index >= 3) {
        if (threadSteps[index]?.stepKind === 'branch'
            && threadSteps[index - 1]?.stepKind === 'tool'
            && threadSteps[index - 2]?.stepKind === 'tool'
            && threadSteps[index - 3]?.stepKind === 'branch') {
            index -= 3;
            continue;
        }
        break;
    }
    return index;
}
export function rebindBranchExit(state, cursor, nodeName) {
    const { currentThreadIndex, currentStepCounter, threadSteps } = cursor;
    const step = threadSteps[currentStepCounter];
    if (!step)
        throw new Error(`[${nodeName}] Step not found at counter ${currentStepCounter}`);
    const isRemainderBranchStep = step.stepKind === 'branch';
    const anchorBranchIndex = (() => {
        if (isRemainderBranchStep)
            return currentStepCounter;
        const prev = currentStepCounter > 0 ? threadSteps[currentStepCounter - 1] : undefined;
        const prevPrev = currentStepCounter > 1 ? threadSteps[currentStepCounter - 2] : undefined;
        const prevPrevPrev = currentStepCounter > 2 ? threadSteps[currentStepCounter - 3] : undefined;
        if (prev?.stepKind === 'tool' && prevPrev?.stepKind === 'tool' && prevPrevPrev?.stepKind === 'branch') {
            return currentStepCounter - 3;
        }
        throw new Error(`[${nodeName}] Expected a BranchStep at counter ${currentStepCounter} or a (BRANCH, WHEN, WHAT, NEXT) boundary pattern`);
    })();
    if (isRemainderBranchStep && step.stepKind !== 'branch') {
        throw new Error(`[${nodeName}] Expected remainder step to be a BranchStep at counter ${currentStepCounter}`);
    }
    const bindingContext = { toolStepByKey: buildToolStepByKeyMapForThread(threadSteps), toolMap: state.toolMap };
    const nextStepCounter = isRemainderBranchStep ? currentStepCounter + 1 : currentStepCounter;
    if (nextStepCounter > threadSteps.length)
        throw new Error(`[${nodeName}] nextStepCounter out of range`);
    const rootIndex = findBranchChainRootIndex(threadSteps, anchorBranchIndex);
    const rootBranch = threadSteps[rootIndex];
    if (!rootBranch || rootBranch.stepKind !== 'branch') {
        throw new Error(`[${nodeName}] Expected a BranchStep at root index ${rootIndex}`);
    }
    if (rootBranch.cases.length === 0)
        throw new Error(`[${nodeName}] Missing root branch cases at index ${rootIndex}`);
    const canonicalDefaultCase = rootBranch.cases[rootBranch.cases.length - 1];
    if (!canonicalDefaultCase)
        throw new Error(`[${nodeName}] Missing canonical default case at root index ${rootIndex}`);
    const canonicalAddress = getSingleNonErrorOutputToolStepRoleAddress(canonicalDefaultCase.what, nodeName);
    const previousStep = threadSteps[currentStepCounter - 1];
    if (!previousStep || previousStep.stepKind !== 'tool') {
        throw new Error(`[${nodeName}] Expected preceding ToolStep (WHAT) before branch exit rebind at ${currentStepCounter}`);
    }
    const winningAddress = getSingleNonErrorOutputToolStepRoleAddress(previousStep, nodeName);
    const { nextWorkflowState, changedToolStepKeys } = rebindDownstreamInternalInputPotentials(state.mutableWorkflow.workflowState, nextStepCounter, threadSteps, bindingContext, (source) => toolStepRoleAddressEquals(source, canonicalAddress) ? winningAddress : undefined);
    const updatedWorkflow = { ...state.mutableWorkflow, workflowState: nextWorkflowState };
    const workflowStateDelta = projectChangedToolStepsToWorkflowStateDelta(updatedWorkflow, changedToolStepKeys, nextWorkflowState);
    const event = {
        tracePointKind: 'tick', workflowHandle: state.mutableWorkflow.handle,
        threadIndex: currentThreadIndex, counterSnapshot: { stepCounterAfter: nextStepCounter },
        stepPath: toTopLevelStepPath(currentThreadIndex, rootIndex),
        stepKind: 'branch', tickKind: 'branch_exit_rebind', macroStepContext: nullMacroStepContext(),
        delta: { workflowStateDelta },
    };
    return {
        mutableWorkflow: updatedWorkflow, stepCounters: { [currentThreadIndex]: nextStepCounter }, currentThreadIndex,
        ...(!isRemainderBranchStep ? {
            branchExitRebindGuardByThreadIndex: { [currentThreadIndex]: currentStepCounter },
        } : {}),
        ...stampAndAppendTracePoint(state, currentThreadIndex, event),
    };
}
export function rebindLoopExit(state, cursor, nodeName) {
    const { currentThreadIndex, currentStepCounter, threadSteps } = cursor;
    const step = threadSteps[currentStepCounter];
    if (!step || (step.stepKind !== 'for' && step.stepKind !== 'while')) {
        throw new Error(`[${nodeName}] Expected a ForStep/WhileStep at counter ${currentStepCounter}`);
    }
    const bindingContext = { toolStepByKey: buildToolStepByKeyMapForThread(threadSteps), toolMap: state.toolMap };
    const rootLoopStepKey = state.rootLoopStepKeyByCloneLoopStepKey?.[step.stepKey] ?? step.stepKey;
    const lineageKey = makeLoopLineageKey(currentThreadIndex, rootLoopStepKey);
    const lineage = state.loopLineageEntryByKey?.[lineageKey];
    if (!lineage)
        throw new Error(`[${nodeName}] Missing loop lineage for ${lineageKey}`);
    const nextStepCounter = currentStepCounter + 1;
    if (nextStepCounter > threadSteps.length)
        throw new Error(`[${nodeName}] nextStepCounter out of range`);
    const { nextWorkflowState, changedToolStepKeys } = rebindDownstreamInternalInputPotentials(state.mutableWorkflow.workflowState, nextStepCounter, threadSteps, bindingContext, (source, stepKey, roleName) => {
        if (source.stepKey === lineage.rootWhenToolStepKey) {
            throw new Error(`[${nodeName}] Downstream steps may not reference loop whenTool outputs (found reference at ${stepKey}:${roleName})`);
        }
        return toolStepRoleAddressEquals(source, lineage.rootWhatOutputAddress)
            ? lineage.latestWhatOutputAddress : undefined;
    });
    const updatedWorkflow = { ...state.mutableWorkflow, workflowState: nextWorkflowState };
    const workflowStateDelta = projectChangedToolStepsToWorkflowStateDelta(updatedWorkflow, changedToolStepKeys, nextWorkflowState);
    const previousStep = currentStepCounter > 0 ? threadSteps[currentStepCounter - 1] : undefined;
    if (!previousStep || previousStep.stepKind !== 'tool') {
        throw new Error(`[${nodeName}] Expected previous step to be a when ToolStep at counter ${currentStepCounter - 1}`);
    }
    const event = {
        tracePointKind: 'tick', workflowHandle: state.mutableWorkflow.handle,
        threadIndex: currentThreadIndex, counterSnapshot: { stepCounterAfter: nextStepCounter },
        stepPath: toTopLevelStepPath(currentThreadIndex, currentStepCounter),
        stepKind: step.stepKind, tickKind: 'loop_exit_rebind', macroStepContext: nullMacroStepContext(),
        delta: { workflowStateDelta },
    };
    return {
        mutableWorkflow: updatedWorkflow, stepCounters: { [currentThreadIndex]: nextStepCounter }, currentThreadIndex,
        ...stampAndAppendTracePoint(state, currentThreadIndex, event),
    };
}
